/**
 * 파일명 : usr0302p2.tsx
 * 설명 : 경력등록 화면
 * 수정내역
 ******************************************************
 * Version    수정일자      수정자             수정내용      
 ******************************************************
 * 1.0       2023-04-08    김시현             최초작성
 */

/******************************************************
 * Import 영역  
 *****************************************************/
/* React */
import React from 'react';
import locale from 'antd/lib/locale/ko_KR';
import dayjs from 'dayjs';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Usr0302p2Action } from '../../../redux/slice/usr/usr0302p2_slice';
import { IPjDevItemState, IPjRoleItemState } from '../../../redux/models/usr/usr0302p2_model';

/* AntD */
import { Input, Layout, Row, Col, Space, Button, Descriptions, Select, DatePicker, ConfigProvider} from 'antd';

/* 업무 */
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';

/********************************************************
 * 전역변수 선언영역
 ********************************************************/
const Usr0302p2 = (props:any) => {
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */
    const dispatch   = useAppDispatch();
    const mmbrItems  = useAppSelector((state) => state.Usr0302p2.mmbrItems);  // 경력등록정보
    const pjRoleList = useAppSelector((state) => state.Usr0302p2.pjRoleList); // 역할(공통코드)
    const pjDevList  = useAppSelector((state) => state.Usr0302p2.pjDevList);  // 개발유형(공통코드)

    /********************************************************
     * Render 변수 선언 및 셋팅영역
     ********************************************************/
    /* AntD */
    const { Content } = Layout;

    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     */
    React.useEffect(() => {

        setPjRoleList(); // 역할(공통코드)
        setIPjDevList(); // 개발유형(공통코드)

        if (props.id && Object.keys(props.id).length > 0 && props.id != 0) {
            fn_Search(); // 조회
        }

        return () => {
            dispatch(Usr0302p2Action.initState());
        }

    }, []);

    /**
     * 기능 : setPjRoleList
     * 설명 : 역할 콤보박스 데이터 조회(공통코드)
     * param : 
     * return : None
     **/
    const setPjRoleList = () => {
        const option = {
            useYn: 'Y',
            callback: (datas:IPjRoleItemState[]) => {
                let arrOptions:IPjRoleItemState[] = [];
                datas.forEach((item:IPjRoleItemState) => {
                    arrOptions.push({
                        label: item.cdNm,
                        value: item.cd
                    });
                });
                dispatch(Usr0302p2Action.setPjRoleList(arrOptions));
            }
        };
        common.getComCodeList('038', option); // pjDevRoleCd(038_개발역할코드)
    }

    /**
     * 기능 : setIPjDevList
     * 설명 : 개발유형 콤보박스 데이터 조회(공통코드)
     * param : 
     * return : None
     **/
    const setIPjDevList = () => {
        const option = {
            useYn: 'Y',
            callback: (datas:IPjDevItemState[]) => {
                let arrOptions:IPjDevItemState[] = [];
                datas.forEach((item:IPjDevItemState) => {
                    arrOptions.push({
                        label: item.cdNm,
                        value: item.cd
                    });
                });
                dispatch(Usr0302p2Action.setPjDevList(arrOptions));
            }
        };
        common.getComCodeList('026', option); // pjDevTypeCd(026_개발유형코드)
    }

    /**
     * 기능 : fn_Search
     * 설명 : 경력등록정보 조회
     * param : 
     * return : None
     **/
    const fn_Search  = () => {

        if (!props || common.isEmptyString(props.id)) {
                dispatch(Usr0302p2Action.setMmbrItems({ ...mmbrItems }));
        }

        if (props && !common.isEmptyString(props.empNo)) {
            /* 변수선언 및 셋팅 영역 */
            // 입력파라미터
            const requestData = {
                empNo: props.empNo,
                id: props.id
            };

            /* 트렌젝션 호출 및 결과 셋팅 */
            commonProxy.callAsyncSvc(requestData, {
                txCd: 'TRUSRR0020',
                success: (responseData:any) => {
                    dispatch(Usr0302p2Action.setMmbrItems(responseData.mmbrItemList[0]));
                },
            });
        }
    };

    /********************************************************
     * 이벤트 함수 영역
     ********************************************************/
    /**
     *  기능 : 프로젝트 시작일자 데이터 변경
     *  설명 : 시작일자 데이터 변경값을 받는다
     *  param :
     *  return : None
     */
    const pjStartDt_onChange = (_: any, dateStrings: any) => {
        dispatch(Usr0302p2Action.setMmbrItems({ ...mmbrItems, pjStartDt : dateStrings }));
    };

    /**
     *  기능 : 프로젝트 종료일자 데이터 변경
     *  설명 : 종료일자 데이터 변경값을 받는다
     *  param :
     *  return : None
     */
    const pjEndDt_onChange = (_: any, dateStrings: any) => {
        dispatch(Usr0302p2Action.setMmbrItems({ ...mmbrItems, pjEndDt : dateStrings }));
    };

    /**
     * 기능 : 초기화버튼 클릭 이벤트
     * 설명 : 초기화
     * param : 
     * return : None
     */
    const btnReset_onClick = () => {
        dispatch(Usr0302p2Action.initState());

        // 상세보기에서 초기화 시, 기존 데이터 재조회
        if (props.id && Object.keys(props.id).length > 0 && props.id != 0) {
            fn_Search(); // 조회
        }
    };

    /**
     * 기능 : 저장버튼 클릭 이벤트
     * 설명 : 등록/수정
     * param : 
     * return : None
     */
    const btnSave_onClick = () => {

        /* 정합성 체크 */
        if (!fn_CheckValidation('save')) {
            return;
        }

        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            ...mmbrItems,
            empNo: props.empNo,
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRUSRS0021',
            success: (_responseData: any) => {
                const param = {
                    title : "위드정보 확인",
                    contents : "정상적으로 처리되었습니다.",
                };
                common.openAlert(param);
            },
        });
    };

    /**
     * 기능 : 삭제버튼 클릭 이벤트
     * 설명 : 삭제
     * param : 
     * return : None
     */
    const btnDelete_onClick = () => {

        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = { ...mmbrItems};

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRUSRS0022',
            success: (_responseData:any) => {
                // 프로젝트정보 없는 경우 삭제되지 않도록
                if (common.isEmptyString(requestData.cusNm) || common.isEmptyString(requestData.pjNm)) {
                    const param = {
                        title : '위드정보 확인',
                        contents: '삭제할 정보가 없습니다.',
                    };
                    common.openAlert(param);
                    return false;
                }
                else
                {
                    const param = {
                        title : '위드정보 확인',
                        contents : '정상적으로 삭제되었습니다.',
                    };
                    common.openAlert(param);
                }
            },
        });
    };
    
    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = (e:any, record?: any) => {

        const buttonName = e.currentTarget.name;

        switch (buttonName) {
            
            // 초기화 버튼
            case "btnReset" : {
                btnReset_onClick();
                break;
            }

            // 닫기 버튼
            case "btnClose" : {
                if (e) {
                    props.accept(record);
                }
                props.cancel();
                break;
            }

            // 저장 버튼
            case 'btnSave': {
                btnSave_onClick();
                break;
            }

            // 삭제 버튼
            case 'btnDelete': {
                btnDelete_onClick();
                break;
            }

            // 프로젝트 불러오기 버튼(usr0302p3_프로젝트 검색 팝업)
            case "btn_PjLoad" : {
                const callback = (data:[]) => {
                    // 프로젝트정보 가져오기
                    dispatch(Usr0302p2Action.setMmbrItems(data));
                }
                const params: any = {};
                params.title      = "프로젝트 검색";
                params.popupId    = "usr0302p3";
                params.accept     = callback;
                params.width      = 1020;
                common.openPopup(params);
                break;
            }
            default:
        }
    };

    /********************************************************
     * 개발자 함수 영역
     ********************************************************/
    /**
     * 기능 : 정합성체크
     * 설명 : 정합성체크
     * param : 
     * return : None
     */
     const fn_CheckValidation = (tran_div:string) => {

        if (tran_div === 'save') {
            let result = true;

            // 필수 입력값 체크
            if(common.isEmptyString(mmbrItems.pjStartDt)) {
                const param = {
                    title : '위드정보 확인',
                    contents: '[프로젝트 시작일자]는 필수입력 항목입니다.',
                };
                common.openInfo(param);
                return false;
            }

            if(common.isEmptyString(mmbrItems.pjEndDt)) {
                const param = {
                    title : '위드정보 확인',
                    contents: '[프로젝트 종료일자]는 필수입력 항목입니다.',
                };
                common.openInfo(param);
                return false;
            }

            if(common.isEmptyString(mmbrItems.pjNm)) {
                const param = {
                    title : '위드정보 확인',
                    contents: '[프로젝트명]은 필수입력 항목입니다.',
                };
                common.openInfo(param);
                return false;
            }

            if(common.isEmptyString(mmbrItems.cusNm)) {
                const param = {
                    title : '위드정보 확인',
                    contents: '[고객사명]은 필수입력 항목입니다.',
                };
                common.openInfo(param);
                return false;
            }
            return result;
        }
    }    

    /********************************************************
     * 화면 디자인 영역(경력관리-개발경력-경력등록 및 상세보기)
     ********************************************************/
    return (
        /* 화면 디자인 영역 */
        <Layout>
            <Row align = "middle" justify = "space-between">
                <Col flex = "auto" style = {{ margin: 10, textAlign: 'right' }}>
                    <Space>
                        <Button className = "background-color-primary-button" name = "btn_PjLoad" onClick = {(e: any) => onClick(e)}>
                            프로젝트 불러오기
                        </Button>
                    </Space>
                </Col>
            </Row>
            <Content style={{minWidth:'100%', minHeight: '100%'}}>
                <Space direction='vertical' size='middle'>
                <Descriptions bordered column={2} size="small">
                    <Descriptions.Item label = "프로젝트 시작일자*">
                        <Space>
                            <ConfigProvider locale = {locale}>
                                <DatePicker
                                    style = {{ width: 275 }}
                                    id = 'tx_calendar'
                                    allowClear = {false}
                                    inputReadOnly
                                    value = {mmbrItems.pjStartDt ? dayjs(mmbrItems.pjStartDt) : null}
                                    onChange = {pjStartDt_onChange}
                                />
                            </ConfigProvider>
                        </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label = "프로젝트 종료일자*">
                        <Space>
                            <ConfigProvider locale={locale}>
                                <DatePicker
                                    style = {{ width: 275 }}
                                    id = 'tx_calendar'
                                    allowClear = {false}
                                    inputReadOnly
                                    value = {mmbrItems.pjEndDt ? dayjs(mmbrItems.pjEndDt) : null}
                                    onChange = {pjEndDt_onChange}
                                />
                            </ConfigProvider>
                        </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="프로젝트명*" span={2} >
                        <Input id="inputPjNm" name="pjNm" style = {{ width: 750 }} value={mmbrItems.pjNm} maxLength = {100}
                        onChange={e => { dispatch(Usr0302p2Action.setMmbrItems({...mmbrItems, pjNm : e.currentTarget.value}))}}/>
                    </Descriptions.Item>
                    <Descriptions.Item label="고객사*" span={2} >
                        <Input id="inputCusNm" name="cusNm" style = {{ width: 750 }} value={mmbrItems.cusNm} maxLength = {100}
                        onChange={e => { dispatch(Usr0302p2Action.setMmbrItems({...mmbrItems, cusNm : e.currentTarget.value}))}}/>
                    </Descriptions.Item>
                    <Descriptions.Item label="업무">
                        <Input id="inputPjDevBizCntnt" name="pjDevBizCntnt" style = {{ width: 275 }} value={mmbrItems.pjDevBizCntnt}
                        onChange={e => { dispatch(Usr0302p2Action.setMmbrItems({...mmbrItems, pjDevBizCntnt : e.currentTarget.value}))}}/>
                    </Descriptions.Item>
                    <Descriptions.Item label="역할">
                        <Select
                            style = {{ width: 275 }}
                            size = 'small'
                            defaultValue = ''
                            optionFilterProp = 'label'
                            options = {pjRoleList}
                            value = {mmbrItems.pjDevRoleCd}
                            onChange = {val => dispatch(Usr0302p2Action.setMmbrItems({...mmbrItems, pjDevRoleCd: val}))}
                        />      
                    </Descriptions.Item>
                    <Descriptions.Item label="개발툴 및 언어(화면)">
                        <Input id="inputPjDevtoolView" name="pjDevtoolView" style = {{ width: 275 }} value={mmbrItems.pjDevtoolView}
                        onChange={e => { dispatch(Usr0302p2Action.setMmbrItems({...mmbrItems, pjDevtoolView : e.currentTarget.value}))}}/>
                    </Descriptions.Item>
                    <Descriptions.Item label="개발툴 및 언어(서비스)">
                        <Input id="inputPjDevtoolSvc" name="pjDevtoolSvc" style = {{ width: 275 }} value={mmbrItems.pjDevtoolSvc}
                        onChange={e => { dispatch(Usr0302p2Action.setMmbrItems({...mmbrItems, pjDevtoolSvc : e.currentTarget.value}))}}/>
                    </Descriptions.Item>
                    <Descriptions.Item label="개발 DB">
                        <Input id="inputPjDevtoolDb" name="pjDevtoolDb" style = {{ width: 275 }} value={mmbrItems.pjDevtoolDb}
                        onChange={e => { dispatch(Usr0302p2Action.setMmbrItems({...mmbrItems, pjDevtoolDb : e.currentTarget.value}))}}/>
                    </Descriptions.Item>
                    <Descriptions.Item label="사용 REPORT">
                        <Input id="inputPjDevtoolRpt" name="pjDevtoolRpt" style = {{ width: 275 }} value={mmbrItems.pjDevtoolRpt}
                        onChange={e => { dispatch(Usr0302p2Action.setMmbrItems({...mmbrItems, pjDevtoolRpt : e.currentTarget.value}))}}/>
                    </Descriptions.Item>
                    <Descriptions.Item label="개발형태(C/S,클라우드 등)">
                    <Input id="inputPjDevformType" name="pjDevformType" style = {{ width: 275 }} value={mmbrItems.pjDevformType}
                        onChange={e => { dispatch(Usr0302p2Action.setMmbrItems({...mmbrItems, pjDevformType : e.currentTarget.value}))}}/>
                    </Descriptions.Item>
                    <Descriptions.Item label="개발유형(SI, SM)">
                        <Select
                            style = {{ width: 275 }}
                            size = 'small'
                            defaultValue = ''
                            optionFilterProp = 'label'
                            options = {pjDevList}
                            value = {mmbrItems.pjDevTypeCd}
                            onChange = {val => dispatch(Usr0302p2Action.setMmbrItems({...mmbrItems, pjDevTypeCd: val}))}
                        />
                    </Descriptions.Item>
                </Descriptions>
                </Space>
                <Row justify='center' style={{ marginTop: 20 }}>
                    <Space size='small'>
                        <Col>
                            <Button
                                name='btnReset'
                                className='background-color-default-button'
                                onClick={(onClick)}
                                >
                                초기화
                            </Button>
                        </Col>
                        <Col>
                        <Button
                            name='btnSave'
                            className='background-color-primary-button'
                            onClick={(onClick)}
                        >
                            저장
                            </Button>
                        </Col>
                        <Col>
                        <Button
                            name='btnDelete'
                            className='background-color-primary-button'
                            onClick={(onClick)}
                            disabled={props.id == '0'} // 경력등록팝업 : 삭제 불가능, 상세보기 : 삭제 가능
                        >
                            삭제
                            </Button>
                        </Col>
                        <Col>
                        <Button
                            name='btnClose'
                            className='background-color-default-button'
                            onClick={(onClick)}
                        >
                            닫기
                        </Button>
                        </Col>
                    </Space>
                </Row>
            </Content>
        </Layout>
    );
}

export default Usr0302p2;